import { isTopNotice, lockNoticeScroll, noticeFocusable, trapNoticeTab } from './noticeAccessibility';
import { ensureNoticeStyles } from './noticeStyles';
export interface NoticeOptions { title?: string; confirmText?: string; cancelText?: string }
const queueKey = Symbol.for('dapr.notice.queue');
type NoticeWindow = Window & { [queueKey]?: Promise<unknown> };
const translate = (key: string, fallback: string): string => {
  const core = (window as unknown as { G7Core?: { t?: (key: string) => string } }).G7Core;
  const value = core?.t?.(key);
  return value && value !== key ? value : fallback;
};
function openNotice(message: string, confirm: boolean, options: NoticeOptions): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);
  return new Promise(resolve => {
    ensureNoticeStyles();
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlock = lockNoticeScroll();
    const overlay = document.createElement('div'); overlay.className = 'notice-overlay';
    const panel = document.createElement('div'); panel.className = 'notice-panel'; panel.tabIndex = -1;
    panel.setAttribute('role', 'alertdialog'); panel.setAttribute('aria-modal', 'true'); panel.dataset.noticePanel = 'true';
    const id = `notice-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const icon = document.createElement('div'); icon.className = 'notice-icon'; icon.setAttribute('aria-hidden','true');
    icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9" fill="currentColor" stroke="none"/><path d="M12 8v5m0 3h.01" stroke="#fff"/></svg>';
    const title = document.createElement('h2'); title.className = 'notice-title'; title.id = `${id}-title`;
    title.textContent = options.title ?? translate(confirm ? 'common.confirm' : 'common.notice', confirm ? '확인' : '알림');
    const body = document.createElement('div'); body.className = 'notice-body notice-message'; body.id = `${id}-message`; body.textContent = message;
    panel.setAttribute('aria-labelledby', title.id); panel.setAttribute('aria-describedby', body.id);
    const actions = document.createElement('div'); actions.className = 'notice-actions';
    const controller = new AbortController(); let settled = false;
    const finish = (result: boolean) => {
      if (settled) return; settled = true; controller.abort(); overlay.remove(); unlock();
      if (previous?.isConnected) previous.focus(); resolve(result);
    };
    const button = (label: string, primary: boolean, result: boolean) => {
      const element = document.createElement('button'); element.type = 'button'; element.className = primary ? 'notice-primary' : 'notice-secondary';
      element.textContent = label; element.addEventListener('click', () => finish(result), { signal: controller.signal }); actions.append(element);
    };
    if (confirm) button(options.cancelText ?? translate('common.cancel', '취소'), false, false);
    button(options.confirmText ?? translate('common.confirm', '확인'), true, true);
    panel.append(icon, title, body, actions); overlay.append(panel); document.body.append(overlay);
    noticeFocusable(panel)[0]?.focus();
    overlay.addEventListener('click', event => { if (event.target === overlay) finish(false); }, { signal: controller.signal });
    document.addEventListener('keydown', event => {
      if (!isTopNotice(panel)) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); finish(false); }
      if (event.key === 'Tab') trapNoticeTab(event, panel);
    }, { capture: true, signal: controller.signal });
  });
}
function enqueue(message: string, confirm: boolean, options: NoticeOptions = {}): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  const owner = window as NoticeWindow;
  const next = (owner[queueKey] ?? Promise.resolve()).then(() => openNotice(message, confirm, options));
  owner[queueKey] = next.then(() => undefined, () => undefined);
  return next;
}
export function confirmNotice(message: string, options?: NoticeOptions): Promise<boolean> { return enqueue(message, true, options); }
export async function alertNotice(message: string, options?: NoticeOptions): Promise<void> { await enqueue(message, false, options); }
